import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import { botName } from '../components/games/spy/logic'

function makeCode() {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789' // no ambiguous chars
  let c = ''
  for (let i = 0; i < 6; i++) c += alphabet[Math.floor(Math.random() * alphabet.length)]
  return c
}

// Online Spy room. Manages create/join by code, realtime sync of the
// game + lobby + roles, host start (deal) / reveal, and voting. Secret
// roles are RLS-scoped: each client only sees its own until reveal.
export function useSpyOnline() {
  const { user } = useAuth()
  const userId = user?.id || null

  const [gameId, setGameId] = useState(null)
  const [game, setGame] = useState(null)
  const [players, setPlayers] = useState([])
  const [roles, setRoles] = useState([]) // my row only, or all after reveal
  const [events, setEvents] = useState([]) // shared bot feed
  const [location, setLocation] = useState(null)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  const channelRef = useRef(null)

  const refetch = useCallback(async (id) => {
    const gid = id || gameId
    if (!gid) return
    const [{ data: g }, { data: ps }, { data: rs }, { data: ev }] = await Promise.all([
      supabase.from('spy_games').select('*').eq('id', gid).maybeSingle(),
      supabase.from('spy_game_players').select('user_id, name, joined_at').eq('game_id', gid).order('joined_at'),
      supabase.from('spy_game_roles').select('user_id, bot_id, is_spy, role_index, location_id, voted_for').eq('game_id', gid),
      supabase.from('spy_game_events').select('id, actor_name, kind, text, created_at').eq('game_id', gid).order('created_at'),
    ])
    setGame(g || null)
    setPlayers(ps || [])
    setRoles(rs || [])
    setEvents(ev || [])
    // Location id comes from a role row that carries it (non-spy rows
    // during play = only my own; after reveal = any). The spy's own
    // row has none, so the spy never resolves the location early.
    const locId = (rs || []).map((r) => r.location_id).find(Boolean) || null
    if (locId) {
      const { data: loc } = await supabase.from('spy_locations').select('*').eq('id', locId).maybeSingle()
      setLocation(loc || null)
    } else {
      setLocation(null)
    }
  }, [gameId])

  // Realtime sync while in a game.
  useEffect(() => {
    if (!gameId) return undefined
    let alive = true
    const channel = supabase
      .channel(`spy-online-${gameId}-${Math.random().toString(36).slice(2)}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'spy_games', filter: `id=eq.${gameId}` }, () => { if (alive) refetch(gameId) })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'spy_game_players', filter: `game_id=eq.${gameId}` }, () => { if (alive) refetch(gameId) })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'spy_game_roles', filter: `game_id=eq.${gameId}` }, () => { if (alive) refetch(gameId) })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'spy_game_events', filter: `game_id=eq.${gameId}` }, () => { if (alive) refetch(gameId) })
      .subscribe()
    channelRef.current = channel
    refetch(gameId)
    return () => { alive = false; supabase.removeChannel(channel) }
  }, [gameId, refetch])

  const create = useCallback(async (name, timerMinutes = 8) => {
    if (!userId) return null
    setBusy(true); setError(null)
    try {
      for (let attempt = 0; attempt < 4; attempt++) {
        const code = makeCode()
        const { data, error: e } = await supabase
          .from('spy_games')
          .insert({ code, host_id: userId, timer_minutes: timerMinutes, status: 'lobby' })
          .select('id')
          .single()
        if (e) { if (e.code === '23505') continue; setError(e.message); return null }
        await supabase.from('spy_game_players').insert({ game_id: data.id, user_id: userId, name: name?.trim() || 'Player' })
        setGameId(data.id)
        return data.id
      }
      setError('code collision')
      return null
    } finally { setBusy(false) }
  }, [userId])

  const join = useCallback(async (code, name) => {
    if (!userId) return null
    setBusy(true); setError(null)
    try {
      const { data: g } = await supabase
        .from('spy_games')
        .select('id, status')
        .eq('code', (code || '').trim().toUpperCase())
        .maybeSingle()
      if (!g) { setError('not_found'); return null }
      if (g.status !== 'lobby') { setError('in_progress'); return null }
      await supabase.from('spy_game_players').upsert(
        { game_id: g.id, user_id: userId, name: name?.trim() || 'Player' },
        { onConflict: 'game_id,user_id' },
      )
      setGameId(g.id)
      return g.id
    } finally { setBusy(false) }
  }, [userId])

  const start = useCallback(async () => {
    if (!gameId) return
    setBusy(true); setError(null)
    const resp = await supabase.functions.invoke('spy-online-deal', { body: { game_id: gameId } })
    setBusy(false)
    if (resp.error) setError('deal_failed')
    await refetch(gameId)
  }, [gameId, refetch])

  const vote = useCallback(async (targetUserId) => {
    if (!gameId || !userId) return
    await supabase.from('spy_game_roles').update({ voted_for: targetUserId }).eq('game_id', gameId).eq('user_id', userId)
    await refetch(gameId)
  }, [gameId, userId, refetch])

  const reveal = useCallback(async () => {
    if (!gameId) return
    await supabase.from('spy_games').update({ status: 'revealed' }).eq('id', gameId)
    await refetch(gameId)
  }, [gameId, refetch])

  const leave = useCallback(async () => {
    if (!gameId || !userId) { setGameId(null); return }
    const isHost = game?.host_id === userId
    if (isHost) {
      await supabase.from('spy_games').update({ status: 'abandoned' }).eq('id', gameId)
    }
    await supabase.from('spy_game_players').delete().eq('game_id', gameId).eq('user_id', userId)
    setGameId(null); setGame(null); setPlayers([]); setRoles([]); setLocation(null)
  }, [gameId, userId, game])

  const myRole = useMemo(() => roles.find((r) => r.user_id === userId) || null, [roles, userId])
  const isHost = game?.host_id === userId
  const bots = useMemo(() => (Array.isArray(game?.bots) ? game.bots : []), [game])

  // Unified seat list (humans + bots) for the vote grid + name lookup.
  const seats = useMemo(() => [
    ...players.map((p) => ({ id: p.user_id, name: p.name, isBot: false })),
    ...bots.map((b) => ({ id: b.id, name: b.name, isBot: true })),
  ], [players, bots])
  const nameOf = useCallback((id) => seats.find((s) => s.id === id)?.name || '—', [seats])

  // Host: add / remove a computer player in the lobby.
  const addBot = useCallback(async () => {
    if (!gameId || !isHost) return
    const id = (crypto?.randomUUID && crypto.randomUUID()) || `bot-${Date.now()}`
    const next = [...bots, { id, name: `🤖 ${botName(bots.length)}` }]
    await supabase.from('spy_games').update({ bots: next }).eq('id', gameId)
    await refetch(gameId)
  }, [gameId, isHost, bots, refetch])

  const removeBot = useCallback(async () => {
    if (!gameId || !isHost || !bots.length) return
    await supabase.from('spy_games').update({ bots: bots.slice(0, -1) }).eq('id', gameId)
    await refetch(gameId)
  }, [gameId, isHost, bots, refetch])

  // Make a bot speak — server resolves its secret role + posts to the
  // shared feed (realtime delivers it to everyone).
  const botTurn = useCallback(async (mode = 'ask', botId = null, question = null) => {
    if (!gameId) return
    await supabase.functions.invoke('spy-online-bot-turn', {
      body: { game_id: gameId, mode, bot_id: botId || undefined, question: question || undefined },
    })
  }, [gameId])

  // Vote tally (only meaningful after reveal, when all role rows are
  // readable). Returns [{ userId, name, votes }] sorted desc + the spy ids.
  const tally = useMemo(() => {
    const counts = new Map()
    for (const r of roles) {
      if (r.voted_for) counts.set(r.voted_for, (counts.get(r.voted_for) || 0) + 1)
    }
    return Array.from(counts.entries())
      .map(([uid, votes]) => ({ userId: uid, name: nameOf(uid), votes }))
      .sort((a, b) => b.votes - a.votes)
  }, [roles, nameOf])
  const spyIds = useMemo(() => roles.filter((r) => r.is_spy).map((r) => r.user_id || r.bot_id), [roles])

  return {
    gameId, game, players, bots, seats, events, location, myRole, isHost, error, busy,
    tally, spyIds, nameOf,
    create, join, start, vote, reveal, leave, refetch,
    addBot, removeBot, botTurn,
  }
}
